import { workHasPendingUserGate } from '@/constants/work';

const statuses = {
  running: { label: 'Working', icon: 'circle-notch', tone: 'accent', spinning: true },
  queued: { label: 'Queued', icon: 'clock', tone: 'secondary' },
  attention: { label: 'Needs attention', icon: 'circle-exclamation', tone: 'warning' },
  interrupted: { label: 'Paused — needs attention', icon: 'pause', tone: 'warning' },
  budget_exceeded: { label: 'Run limit reached — needs attention', icon: 'battery-empty', tone: 'warning' },
  idle: { label: 'Finished', icon: 'circle-check', tone: 'success' },
  read: { label: 'Finished', hidden: true },
  failed: { label: 'Failed — needs attention', icon: 'circle-xmark', tone: 'destructive' },
  stopped: { label: 'Stopped', hidden: true },
};
const pendingByEvents = new WeakMap();
const tasksByConversation = new WeakMap();

export function sidebarStatusEvents(events) {
  return (events || []).filter(ev => (
    /^session_(start|resume|end)$/.test(ev?.type)
    || /^browser_(login|confirmation)_/.test(ev?.type)
    || /^user_question_/.test(ev?.type)
  ));
}

export function sidebarChatTask(state, chat, linkedTaskId) {
  const id = linkedTaskId || sidebarTaskId(chat.taskId) || sidebarTaskId(chat.coworkerSession);
  if (id && state.tasks[id]) return state.tasks[id];
  // Several selectors per sidebar row run on every Work draft update. Build
  // the fallback lookup once per immutable tasks snapshot, not once per row.
  let byConversation = tasksByConversation.get(state.tasks);
  if (!byConversation) {
    byConversation = new Map();
    for (const task of Object.values(state.tasks)) {
      const conversationId = String(task.conversationId || task.conversation || '');
      if (!byConversation.has(conversationId)) byConversation.set(conversationId, task);
    }
    tasksByConversation.set(state.tasks, byConversation);
  }
  return byConversation.get(String(chat.id));
}

const hasNewerHistory = (chat, task) => chat?.status && task?.updatedAt && chat.updatedAt
  && new Date(chat.updatedAt) > new Date(task.updatedAt);

export function sidebarCompletionTime(chat, task) {
  if (hasNewerHistory(chat, task)) return new Date(chat.finishedAt || chat.updatedAt || 0).getTime() || 0;
  return new Date(task?.finishedAt || chat?.finishedAt || task?.updatedAt || chat?.updatedAt || 0).getTime() || 0;
}

export function sidebarTaskId(value) {
  return value && (typeof value === 'object' ? value.id || value._id : String(value)) || null;
}

// Return shared objects: token/credit updates must not rerender every row.
export function sidebarChatStatus({ chat, task, events, generating = false, viewing = false, readAt = 0, unreadCount = 0, nativePending = false }) {
  const historyIsNewer = hasNewerHistory(chat, task);
  const status = historyIsNewer ? chat.status : task?.status || chat?.status;
  let pending = false;
  if (!historyIsNewer && events?.length) {
    if (!pendingByEvents.has(events)) {
      const lastInteraction = sidebarStatusEvents(events).at(-1);
      // A short cold snapshot may begin after the original question card.
      const waitingOnUser = lastInteraction?.type === 'session_end' && lastInteraction.reason === 'waiting_on_user';
      pendingByEvents.set(events, workHasPendingUserGate(events) || waitingOnUser);
    }
    pending = pendingByEvents.get(events);
  }
  // Explicit Stop cancels gates even before the closing event arrives.
  if (status === 'stopped') return statuses.stopped;
  if (status === 'failed') return statuses.failed;
  if (nativePending || pending || status === 'awaiting_input') return statuses.attention;
  if (status === 'completed' || status === 'idle') {
    const finishedAt = sidebarCompletionTime(chat, task);
    const unseen = finishedAt ? finishedAt > readAt : unreadCount > 0;
    return !viewing && unseen ? statuses.idle : statuses.read;
  }
  if (statuses[status]) return statuses[status];
  return generating ? statuses.running : null;
}
